import React, { Component, useCallback, useEffect, useState } from 'react';
import { css } from '@emotion/react';
import useEmblaCarousel from 'embla-carousel-react';

import Image from '../atoms/Image';
import ArrowButton from '../atoms/ArrowButton';
import Hstack3CellExtremity from '../atoms/Hstack3CellExtremity';
import TitleTextGroup from './TitleTextGroup';

import * as InternalPropTypes from '../../../constants/internal-types';
import * as Breakpoints from '../../../constants/breakpoints';
import ScreenSize from '../../../constants/screenSize';
import { SizesType } from '../../../types/ts/imgixQuery';
import NwLink from '../NwLink';
import Title from '../atoms/Title';
import TitleTypes from '../../../constants/title-types';
import {
  BackgroundColorEnum,
  TypeBackgroundColorTypes,
  TypeSEOWeightTypes,
} from '../../../constants/cms-constants';
import SEOWeights from '../../../constants/cms/control-options/seo-weights';
import { getLowerSEOWeight, getSEOWeight } from '../../../utils/SEOWeight';
import font from '../../../constants/fonts';

const styles = css({
  '& .caption': {
    opacity: '0.4',
  },
  '& .hstack-3-cell-extremety': {
    [Breakpoints.Mobile.mq]: {
      marginBottom: '16px',
    },
    [Breakpoints.TabletUp.mq]: {
      marginBottom: '32px',
    },
  },
  '& .image-container': {
    width: '25%',
  },
  '& .image': {
    transform: 'scale(1.0)',
    transition: 'all 200ms linear',
    display: 'block',
    width: '100%',
    objectFit: 'scale-down',
    overflow: 'hidden',
    height: '100%',

    [Breakpoints.Mobile.mq]: {
      paddingLeft: '15px',
      paddingRight: '15px',
      height: '125px',
    },
    [Breakpoints.Tablet.mq]: {
      paddingLeft: '20px',
      paddingRight: '20px',
      height: '175px',
    },
    [Breakpoints.DesktopUp.mq]: {
      paddingLeft: '25px',
      paddingRight: '25px',
      height: '225px',
    },
  },
  '& .buttons': {
    display: 'flex',
    height: '100%',
    alignItems: 'flex-end',
    '& button': {
      marginLeft: '8px',
    },
  },
  '.embla__viewport': {
    overflow: 'hidden',
    marginBottom: '20px',
  },
  '.embla__container': {
    backfaceVisibility: 'hidden',
    display: 'flex',
    alignItems: 'top',
    touchAction: 'pan-y',
    marginLeft: '-25px',
    marginRight: '-25px',
  },
  '.embla__slide': {
    display: 'flex',
    justifyContent: 'center',
    flex: '0 0 25%',
    minWidth: 0,
    '& .picture': {
      display: 'contents',
    },
    '& :hover': {
      '& .body-text': {
        textDecoration: 'underline',
      },
      '& .image': {
        transform: 'scale(1.05)',
        transition: 'all 200ms linear',
      },
    },
  },
  '& .text_field': {
    marginLeft: '25px',
    marginRight: '25px',
    verticalAlign: 'bottom',
    '& .body-text': {
      ...font.GNBold,
    },
  },
  '& .scroll': {
    width: '100%',
    display: 'flex',
    height: '2px',
    [Breakpoints.Mobile.mq]: {
      marginBottom: '56px',
    },
    [Breakpoints.Tablet.mq]: {
      marginBottom: '64px',
    },
    [Breakpoints.DesktopUp.mq]: {
      marginBottom: '72px',
    },
    '& .active': {
      transition: 'margin 400ms',
      height: '2px',
      backgroundColor: BackgroundColorEnum.BLACK,
    },
  },
});

type ImageCarouselProps = {
  headerText: InternalPropTypes.RichText;
  mediaItems?: InternalPropTypes.MediaItem[];
  backgroundColor?: TypeBackgroundColorTypes;
  seoWeight?: TypeSEOWeightTypes;
};

type EmblaCarouselProps = {
  headerText: InternalPropTypes.RichText;
  mediaItems: InternalPropTypes.MediaItem[];
  sizes: SizesType;
  scrollWidth: number;
  itemsPerSlide: number;
  seoWeight: TypeSEOWeightTypes;
};

function GroupImages(mediaItems, itemsPerSlide) {
  return (
    (mediaItems.length >= itemsPerSlide &&
      [mediaItems.splice(0, itemsPerSlide)].concat(
        GroupImages(mediaItems, itemsPerSlide),
      )) ||
    []
  );
}

function EmblaCarousel(props: EmblaCarouselProps) {
  // eslint-disable-next-line react/prop-types
  const {
    headerText,
    mediaItems,
    sizes,
    scrollWidth,
    itemsPerSlide,
    seoWeight,
  } = props;
  const [emblaRef, emblaApi] = useEmblaCarousel({
    align: 'start',
  });
  const [scrollProgress, setScrollProgress] = useState(0);

  const onPrevButtonClick = useCallback(() => {
    if (!emblaApi) return;
    emblaApi.scrollPrev();
  }, [emblaApi]);

  const onNextButtonClick = useCallback(() => {
    if (!emblaApi) return;
    emblaApi.scrollNext();
  }, [emblaApi]);

  const onScroll = useCallback(
    (emblaApi) => {
      const progress = scrollWidth * emblaApi.selectedScrollSnap();
      setScrollProgress(progress);
    },
    [scrollWidth],
  );

  useEffect(() => {
    if (!emblaApi) return;

    onScroll(emblaApi);
    emblaApi.on('reInit', onScroll);
    emblaApi.on('scroll', onScroll);
  }, [emblaApi, onScroll]);

  const showNavigation = mediaItems.length > itemsPerSlide;
  const addSEOWeight = getSEOWeight(seoWeight, seoWeight);
  const lowerSEOWeight = getLowerSEOWeight(seoWeight);

  return (
    <>
      <Hstack3CellExtremity
        onRenderLeftCell={() => (
          <Title type={TitleTypes.SECTION} seoWeight={addSEOWeight}>
            {headerText}
          </Title>
        )}
        onRenderRightCell={() => (
          <div className="buttons">
            {showNavigation && (
              <ArrowButton onClick={onPrevButtonClick} direction="left" />
            )}
            {showNavigation && <ArrowButton onClick={onNextButtonClick} />}
          </div>
        )}
      />

      <div className="embla">
        <div className="embla__viewport" ref={emblaRef}>
          <div className="embla__container">
            {mediaItems.map((media) => (
              /* eslint-disable-next-line react/no-array-index-key, prefer-template */
              <div className="embla__slide" key={media.link?.name}>
                <NwLink
                  to={media.link?.linkTo}
                  openInNewWindowOrTab={media.link?.openInNewWindowOrTab}
                >
                  <Image image={media?.image} sizes={sizes} />
                  <div className="text_field">
                    <TitleTextGroup
                      bodyText={media.header}
                      seoWeight={lowerSEOWeight}
                    />
                  </div>
                </NwLink>
              </div>
            ))}
          </div>
        </div>
        <div className="scroll">
          <div
            className="active"
            style={{
              width: `${scrollWidth}%`,
              marginLeft: `${scrollProgress}%`,
            }}
          />
        </div>
      </div>
    </>
  );
}

class MediaCarousel extends Component<ImageCarouselProps> {
  static defaultProps = {
    mediaItems: [],
    backgroundColor: null,
    seoWeight: SEOWeights.H2,
  };

  render() {
    const { headerText, mediaItems, backgroundColor, seoWeight } = this.props;
    const itemsPerSlide = 4;

    const scrollWidth = 100 / (mediaItems.length - (itemsPerSlide - 1));
    const sizes: SizesType = {
      [ScreenSize.Mobile]: { w: '125', h: '125', ar: '1:1' },
      [ScreenSize.Tablet]: { w: '175', h: '175', ar: '1:1' },
      [ScreenSize.Desktop]: { w: '225', h: '225', ar: '1:1' },
    };

    const addStyle = backgroundColor ? { background: backgroundColor } : {};

    return (
      <div className="media-carousel-molecule" css={[styles, addStyle]}>
        <EmblaCarousel
          headerText={headerText}
          mediaItems={mediaItems}
          sizes={sizes}
          scrollWidth={scrollWidth}
          itemsPerSlide={itemsPerSlide}
          seoWeight={seoWeight}
        />
      </div>
    );
  }
}

export default MediaCarousel;
